"use client";

import { useEffect, useRef, useState } from 'react';
import { useTenantContext } from '@/contexts/TenantContext';
import type { Participante } from './equipe';

// Tipo correto para imóveis com multi-tenancy
type Imovel = {
  id: string;
  tenant_id: string;
  agent_id: string | null;
  title: string;
  description: string | null;
  property_type: string;
  transaction_type: string;
  price: number;
  condominium_fee: number | null;
  property_tax: number | null;
  total_area: number | null;
  built_area: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parking_spaces: number | null;
  street_address: string | null;
  neighborhood: string;
  city: string;
  state: string | null;
  zip_code: string | null;
  latitude: number | null;
  longitude: number | null;
  images: string[] | null;
  features: any | null;
  property_code: string | null;
  status: string | null;
  is_featured: boolean | null;
  is_exclusive: boolean | null;
  is_luxury: boolean | null;
  condominium_features: any | null;
  views_count: number | null;
  building_name: string | null;
  parceria: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  facebook_catalog_enabled: boolean | null;
  owner_id: string | null;
  /** Captador principal (corretor da equipe). Só vem com sessão. */
  captador_agent_id?: string | null;
  /** Os demais da equipe (corretores, proprietários, captadores). Só vem com sessão. */
  participantes?: Participante[] | null;
  videos: string[] | null;
  olx_catalog_enabled: boolean | null;
};

type Agent = {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  creci: string | null;
  profile_image: string | null;
};

// Criar um tipo que representa a combinação dos dois
export type ImovelWithAgent = Imovel & {
  agent: Agent | null;
};

/**
 * 500 ms, o mesmo de `useBuscaRemota` e `LeadsFiltersBar` (lá está o porquê):
 * digitação normal fica em torno de duas teclas por segundo, então atraso
 * menor ainda dispara uma consulta por tecla. A cota `imoveis:busca` foi
 * calibrada contando com este atraso.
 */
export const ATRASO_DA_BUSCA_MS = 500;

export const useImoveis = (
  initialLimit = 6,
  filters: {
    search?: string;
    propertyType?: string;
    priceRange?: string;
    bedrooms?: string;
    bathrooms?: string;
    city?: string;
    transactionType?: string;
    minPrice?: string;
    maxPrice?: string;
    features?: string[];
    parkingSpots?: string;
    // 🎯 Filtros de landing pages (Google Ads) — semântica gte/lte
    bedroomsMin?: string;
    bathroomsMin?: string;
    suitesMin?: string;
    parkingMin?: string;
    areaMin?: string;
    areaMax?: string;
    condominioMin?: string;
    condominioMax?: string;
    isLuxury?: boolean;
    featured?: boolean; // home: somente destaques (+fallback server-side com recentes)
  } = {},
  incrementLimit?: number,
  initialData?: ImovelWithAgent[],
  initialTotal?: number
) => {
  const scrollLimit = incrementLimit || initialLimit;

  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 useImoveis - Hook iniciado com:', {
      initialLimit,
      incrementLimit: scrollLimit,
      filters
    });
    console.log('🔍 useImoveis - Status filtro será:', 'active');
  }

  // 🌐 USAR TENANT CONTEXT PARA SUBDOMAIN CORRETO
  const { tenant } = useTenantContext();

  const [imoveis, setImoveis] = useState<ImovelWithAgent[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData || initialData.length === 0);
  const [total, setTotal] = useState(initialTotal || 0);
  const pageRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  /** Mensagem quando a falha tem causa que o visitante consegue resolver. */
  const [erro, setErro] = useState<string | null>(null);
  const isFirstMount = useRef(true);

  /**
   * O termo que de fato vai para a API — `filters.search` com atraso.
   *
   * Sem isto, cada tecla era uma requisição: "jardins frança" disparava 14, e a
   * busca tem cota de 30/min por IP (`imoveis:busca`), calibrada justamente para
   * uso COM atraso. Duas ou três buscas digitadas bastavam para o visitante ver
   * "Muitas buscas em pouco tempo" e a lista sumir (produção, 17/10/2026).
   *
   * Nasce igual ao termo para o mount não ver "mudança": as páginas de bairro
   * já chegam com busca e com os dados do SSR. Sem espaços nas pontas: "casa "
   * e "casa" são a mesma busca, e "   " não é busca nenhuma.
   */
  const termo = filters.search?.trim() ?? '';
  const [buscaAplicada, setBuscaAplicada] = useState(termo);
  // Esvaziar (o "x", o "Limpar") é intenção completa e vale NESTE render, não
  // num efeito: assim sai no mesmo commit em que os outros filtros mudam. Por
  // efeito, a busca de baixo rodava antes, ainda com o termo antigo — na home,
  // "Limpar" pedia `search=jardins&featured=true`, uma varredura inútil.
  if (!termo && buscaAplicada) setBuscaAplicada('');
  useEffect(() => {
    if (termo === buscaAplicada) return;
    const timer = setTimeout(() => setBuscaAplicada(termo), ATRASO_DA_BUSCA_MS);
    return () => clearTimeout(timer);
  }, [termo, buscaAplicada]);
  /**
   * "Só destaques" (home) vale enquanto não há busca APLICADA. O chamador manda
   * `featured` sem olhar a busca: se olhasse o texto digitado, a primeira tecla
   * já viraria o filtro e pediria o catálogo inteiro, rotulado com o termo pela
   * metade, antes de a busca de verdade sair.
   */
  const soDestaques = !!filters.featured && !buscaAplicada;
  const filtrosAplicados = { ...filters, search: buscaAplicada || undefined, featured: soDestaques };

  /**
   * Geração da busca na tela. Cada página 0 abre uma nova; resposta de geração
   * anterior que chegue depois é DESCARTADA — inclusive um 429, que apagaria o
   * resultado certo. Mesmo padrão do `pedido` de `useBuscaRemota`. A rolagem
   * infinita (página > 0) herda a geração corrente, sem abrir outra.
   */
  const geracaoAtual = useRef(0);

  const fetchImoveis = async (pageToFetch = 0, limit = initialLimit, filtros = filtrosAplicados) => {
    // ⚠️ AGUARDAR TENANT ESTAR DISPONÍVEL
    if (!tenant) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏳ useImoveis - Aguardando tenant carregar...');
      }
      setLoading(true);
      return;
    }

    const geracao = pageToFetch === 0 ? ++geracaoAtual.current : geracaoAtual.current;
    // Depois de cada `await`: se outra busca começou nesse meio-tempo, esta
    // resposta não é mais a da tela. Sai sem tocar em lista, erro nem loading,
    // que agora pertencem à busca nova.
    const obsoleta = () => geracao !== geracaoAtual.current;

    setLoading(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 useImoveis - fetchImoveis chamado com filtros:', filtros);
      }

      // Converter priceRange ('0-300000') em minPrice/maxPrice se não tiver valores diretos
      let effectiveMinPrice = filtros.minPrice || '';
      let effectiveMaxPrice = filtros.maxPrice || '';

      if (!effectiveMinPrice && !effectiveMaxPrice && filtros.priceRange && filtros.priceRange !== 'all') {
        const parts = filtros.priceRange.split('-');
        if (parts.length === 2) {
          effectiveMinPrice = parts[0] !== '0' ? parts[0] : '';
          // 999999999 é o valor sentinela para "sem máximo" (faixa "Acima de X")
          effectiveMaxPrice = parts[1] !== '999999999' ? parts[1] : '';
        }
      }

      const params = new URLSearchParams({
        page: String(pageToFetch),
        limit: String(limit),
        status: 'active',
        search: filtros.search || '',
        propertyType: filtros.propertyType || 'all',
        bedrooms: filtros.bedrooms || 'all',
        bathrooms: filtros.bathrooms || 'all',
        city: filtros.city || 'Todas as Cidades',
        transactionType: filtros.transactionType || 'all',
        minPrice: effectiveMinPrice,
        maxPrice: effectiveMaxPrice,
        parkingSpots: filtros.parkingSpots || 'all',
      });

      // Adicionar features como parâmetros múltiplos
      if (filtros.features && filtros.features.length > 0) {
        filtros.features.forEach(f => params.append('features', f));
      }

      // 🎯 Filtros de landing pages (gte/lte) — só enviados quando presentes
      if (filtros.bedroomsMin) params.set('bedroomsMin', filtros.bedroomsMin);
      if (filtros.bathroomsMin) params.set('bathroomsMin', filtros.bathroomsMin);
      if (filtros.suitesMin) params.set('suitesMin', filtros.suitesMin);
      if (filtros.parkingMin) params.set('parkingMin', filtros.parkingMin);
      if (filtros.areaMin) params.set('areaMin', filtros.areaMin);
      if (filtros.areaMax) params.set('areaMax', filtros.areaMax);
      if (filtros.condominioMin) params.set('condominioMin', filtros.condominioMin);
      if (filtros.condominioMax) params.set('condominioMax', filtros.condominioMax);
      if (filtros.isLuxury) params.set('isLuxury', 'true');
      if (filtros.featured) params.set('featured', 'true');

      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Parâmetros enviados para API:', params.toString());
      }

      // 🌐 USAR SUBDOMAIN DO TENANT CONTEXT (funciona para todos os cenários)
      // O TenantContext já carrega o tenant correto via middleware + API
      const currentSubdomain = tenant.slug || tenant.subdomain || '';

      if (!currentSubdomain) {
        console.error('⚠️ ERRO: Subdomain não encontrado no TenantContext!', { tenant });
        throw new Error('Subdomain não disponível');
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Subdomain do TenantContext:', currentSubdomain);
        console.log('🔍 Tenant atual:', tenant.nome);
      }

      const res = await fetch(`/api/v1/imoveis?${params.toString()}`, {
        headers: {
          'x-tenant-subdomain': currentSubdomain
        }
      });
      if (obsoleta()) return;

      if (!res.ok) {
        // 429 tem causa e solução próprias. Sem distinguir, o visitante via
        // "Nenhum imóvel encontrado" — igualzinho a uma busca que de fato não
        // casou — e tentava de novo na hora, que é o que não resolve. A rota se
        // dá ao trabalho de responder 429 com o motivo; jogar fora aqui
        // anularia isso.
        throw new Error(res.status === 429 ? 'RATE_LIMIT' : `HTTP error! status: ${res.status}`);
      }

      const json = await res.json();
      if (obsoleta()) return;
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Resposta da API:', json);
        console.log('🔍 Total de imóveis retornados:', Array.isArray(json) ? json.length : json.imoveis?.length || 0);
      }

      let data: ImovelWithAgent[] = [];
      let apiTotal = 0;
      if (Array.isArray(json)) {
        data = json as ImovelWithAgent[];
        apiTotal = data.length;
      } else if (Array.isArray(json.imoveis)) {
        data = json.imoveis as ImovelWithAgent[];
        apiTotal = typeof json.total === 'number' ? json.total : data.length;
      }

      // Converter campos numéricos que vêm como string da API
      data = data.map(imovel => ({
        ...imovel,
        price: typeof imovel.price === 'string' ? parseFloat(imovel.price) : imovel.price,
        bedrooms: typeof imovel.bedrooms === 'string' ? parseInt(imovel.bedrooms) : imovel.bedrooms,
        bathrooms: typeof imovel.bathrooms === 'string' ? parseInt(imovel.bathrooms) : imovel.bathrooms,
        parking_spaces: typeof imovel.parking_spaces === 'string' ? parseInt(imovel.parking_spaces) : imovel.parking_spaces,
        total_area: typeof imovel.total_area === 'string' ? parseFloat(imovel.total_area) : imovel.total_area,
        built_area: typeof imovel.built_area === 'string' ? parseFloat(imovel.built_area) : imovel.built_area,
      }));

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Dados processados: ${data.length} imóveis, total: ${apiTotal}`);
      }
      setTotal(apiTotal);
      if (pageToFetch === 0) {
        setImoveis(data);
      } else {
        setImoveis(prev => {
          const all = [...prev, ...data];
          const unique = Array.from(new Map(all.map(i => [i.id, i])).values());
          return unique;
        });
      }
      setHasMore((data?.length ?? 0) === limit);
      setErro(null);
    } catch (error) {
      if (obsoleta()) return;
      // Página seguinte da rolagem que falha (inclusive por cota) não apaga o
      // que já está na tela: só para de carregar mais. Zerar aqui trocava os
      // cards que o visitante estava vendo pelo aviso de limite.
      if (pageToFetch > 0) {
        setHasMore(false);
        console.error('Error fetching more imoveis:', error);
        setLoading(false);
        return;
      }
      setImoveis([]);
      setHasMore(false);
      setErro(
        error instanceof Error && error.message === 'RATE_LIMIT'
          ? 'Muitas buscas em pouco tempo. Aguarde alguns segundos e tente de novo.'
          : null,
      );
      console.error('Error fetching imoveis:', error);
    }
    setLoading(false);
  };

  // Refaz busca ao alterar filtros OU quando tenant carregar
  useEffect(() => {
    if (tenant) {
      // Pular o fetch inicial se já temos dados SSR
      if (isFirstMount.current && initialData && initialData.length > 0) {
        isFirstMount.current = false;
        return;
      }
      isFirstMount.current = false;
      fetchImoveis(0, initialLimit, filtrosAplicados);
      pageRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenant?.id, // ✅ Adicionar tenant como dependência
    initialLimit,
    buscaAplicada, // não `filters.search`: este espera a digitação parar
    filters.propertyType,
    filters.priceRange,
    filters.bedrooms,
    filters.bathrooms,
    filters.city,
    filters.transactionType,
    filters.minPrice,
    filters.maxPrice,
    filters.parkingSpots,
    filters.bedroomsMin,
    filters.bathroomsMin,
    filters.suitesMin,
    filters.parkingMin,
    filters.areaMin,
    filters.areaMax,
    filters.condominioMin,
    filters.condominioMax,
    filters.isLuxury,
    soDestaques,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.features),
  ]);

  const fetchMore = () => {
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    if (process.env.NODE_ENV === 'development') {
      console.log(`📜 fetchMore: Carregando página ${nextPage} com ${scrollLimit} imóveis`);
    }
    // Termo aplicado, não o que está no campo: a próxima página tem de ser da
    // busca que está na tela, e não de um termo digitado pela metade.
    fetchImoveis(nextPage, scrollLimit, filtrosAplicados); // ✅ Usar scrollLimit para próximas páginas
  };

  // `buscaAplicada`: o termo dos resultados na tela, que pode estar atrás do
  // campo durante a digitação. Rótulo de "N encontrados para X" usa este.
  return { imoveis, loading, hasMore, fetchMore, total, erro, buscaAplicada };
} 